import * as v from 'valibot'

/**
 * Story 短剧（A 域）契约：已提交来源的准备、本人路线清单、季集目录与季管理命令。
 *
 * 关键口径（与实现同源）：
 * - 准备结果三态：prepared / insufficient_content / stale_or_forbidden；没有戏剧证据不硬切、不抢首免。
 * - 集只有 prepared 状态；能否拍、是否扣费、媒体与通知不在本契约。
 * - `est_duration_ms` 一律 null、`duration_estimate` 为 unverified：30–60 秒成片由 B 的逐镜分镜与锁价门禁约束，
 *   A 只记录来源量（char_count/segment_count/turn_count）与审核提示（review），不按散文朗读猜时长。
 */

const StoryFilmId = v.pipe(v.string(), v.minLength(1), v.maxLength(64))
const StoryFilmHash = v.pipe(v.string(), v.minLength(1), v.maxLength(80))
const IsoTime = v.pipe(v.string(), v.isoTimestamp())

export const StoryFilmPrepareBody = v.object({
  run_id: StoryFilmId,
  branch_id: v.optional(StoryFilmId),
  through_turn_id: v.optional(StoryFilmId),
  request_key: v.optional(v.pipe(v.string(), v.minLength(1), v.maxLength(80))),
})

const StoryFilmMaterialFacts = v.object({
  turn_count: v.number(),
  segment_count: v.number(),
  char_count: v.number(),
  scene_count: v.number(),
  cast_ids: v.array(v.string()),
  location_ids: v.array(v.string()),
  est_duration_ms: v.nullable(v.number()),
})

const StoryFilmReview = v.object({
  long_source: v.boolean(),
  review_char_warn: v.number(),
})

const StoryFilmEvidence = v.object({
  events: v.array(v.object({
    event_id: v.nullable(v.string()),
    seq: v.number(),
    evidence_ids: v.array(v.string()),
  })),
  threads: v.array(v.object({
    thread_id: v.string(),
    kind: v.nullable(v.string()),
    status: v.string(),
    through_seq: v.number(),
    evidence_ids: v.array(v.string()),
  })),
})

const StoryFilmEpisode = v.object({
  episode_id: StoryFilmId,
  season_id: StoryFilmId,
  season_no: v.number(),
  episode_no: v.number(),
  from_seq: v.number(),
  to_seq: v.number(),
  from_turn_id: StoryFilmId,
  to_turn_id: StoryFilmId,
  source_version: StoryFilmHash,
  snapshot_id: StoryFilmHash,
  est_duration_ms: v.nullable(v.number()),
  settlement: v.picklist(['result', 'suspense', 'result_and_suspense']),
  review: StoryFilmReview,
  material_facts: StoryFilmMaterialFacts,
})

const StoryFilmTail = v.object({
  from_seq: v.number(),
  to_seq: v.number(),
  char_count: v.number(),
  reason: v.string(),
})

const StoryFilmPrepared = v.object({
  status: v.literal('prepared'),
  film_id: StoryFilmId,
  film_title: v.string(),
  play_language: v.string(),
  branch_id: StoryFilmId,
  season_id: StoryFilmId,
  season_no: v.number(),
  season_title: v.string(),
  cut_policy_version: v.string(),
  duration_estimate: v.literal('unverified'),
  committed_through_seq: v.number(),
  prepared_through_seq: v.number(),
  source_version: StoryFilmHash,
  episodes: v.array(StoryFilmEpisode),
  pending_tail: v.nullable(StoryFilmTail),
})

const StoryFilmInsufficient = v.object({
  status: v.literal('insufficient_content'),
  reason: v.string(),
  committed_through_seq: v.number(),
  after_seq: v.optional(v.number()),
  char_count: v.optional(v.number()),
  from_seq: v.optional(v.nullable(v.number())),
  to_seq: v.optional(v.nullable(v.number())),
})

const StoryFilmStale = v.object({
  status: v.literal('stale_or_forbidden'),
  reason: v.string(),
})

export const StoryFilmPrepareResult = v.variant('status', [StoryFilmPrepared, StoryFilmInsufficient, StoryFilmStale])
export const parseStoryFilmPrepareResult = (value) => v.parse(StoryFilmPrepareResult, value)

export const StoryFilmRouteItem = v.object({
  run_id: StoryFilmId,
  pack_id: StoryFilmId,
  pack_title: v.string(),
  play_language: v.string(),
  run_status: v.string(),
  archived: v.boolean(),
  turn_count: v.number(),
  branch_id: v.nullable(v.string()),
  committed_through_seq: v.number(),
  source_version: v.nullable(StoryFilmHash),
  last_event_at: v.nullable(IsoTime),
  film_id: v.nullable(v.string()),
  prepared_through_seq: v.number(),
  episode_count: v.number(),
  has_new_prose: v.boolean(),
  can_prepare: v.boolean(),
  reason: v.picklist(['ready', 'no_recorded_prose', 'nothing_new', 'below_min_turns']),
  last_played_at: IsoTime,
})

export const StoryFilmRouteList = v.object({
  items: v.array(StoryFilmRouteItem),
  next_cursor: v.nullable(v.string()),
  limit: v.number(),
  /** 准备拍摄要求的最少已提交回合数。 */
  min_turns: v.number(),
})
export const parseStoryFilmRouteList = (value) => v.parse(StoryFilmRouteList, value)

export const StoryFilmCatalogQuery = v.object({
  run_id: StoryFilmId,
  branch_id: v.optional(StoryFilmId),
})

export const StoryFilmCatalog = v.object({
  run_id: StoryFilmId,
  branch_id: v.nullable(v.string()),
  film: v.nullable(v.object({
    film_id: StoryFilmId,
    title: v.string(),
    play_language: v.string(),
    cut_policy_version: v.string(),
  })),
  seasons: v.array(v.object({
    season_id: StoryFilmId,
    film_id: StoryFilmId,
    season_no: v.number(),
    title: v.string(),
    status: v.picklist(['open', 'closed']),
    closed_at: v.nullable(IsoTime),
    updated_at: v.nullable(IsoTime),
  })),
  episodes: v.array(v.object({
    episode_id: StoryFilmId,
    season_id: StoryFilmId,
    season_no: v.nullable(v.number()),
    episode_no: v.number(),
    from_seq: v.number(),
    to_seq: v.number(),
    from_turn_id: StoryFilmId,
    to_turn_id: StoryFilmId,
    source_version: StoryFilmHash,
    snapshot_id: StoryFilmHash,
    est_duration_ms: v.nullable(v.number()),
    settlement: v.nullable(v.picklist(['result', 'suspense', 'result_and_suspense'])),
    cut_policy_version: v.string(),
    prepared_at: IsoTime,
  })),
  source: v.nullable(v.object({
    branch_id: StoryFilmId,
    committed_through_seq: v.number(),
    source_version: StoryFilmHash,
    event_key: v.nullable(StoryFilmHash),
    at: v.nullable(IsoTime),
  })),
  prepared_through_seq: v.number(),
  /** 准备拍摄要求的最少已提交回合数。 */
  min_turns: v.number(),
})
export const parseStoryFilmCatalog = (value) => v.parse(StoryFilmCatalog, value)

export const StoryFilmSeasonRenameBody = v.object({
  title: v.pipe(v.string(), v.minLength(1), v.maxLength(200)),
  expected_updated_at: v.optional(v.nullable(IsoTime)),
})

export const StoryFilmSeasonCloseBody = v.object({
  expected_updated_at: v.optional(v.nullable(IsoTime)),
})

export const StoryFilmSeasonCreateBody = v.optional(v.object({}), {})

export const StoryFilmSeason = v.object({
  season_id: StoryFilmId,
  film_id: StoryFilmId,
  season_no: v.number(),
  title: v.string(),
  status: v.picklist(['open', 'closed']),
  closed_at: v.nullable(IsoTime),
  updated_at: v.nullable(IsoTime),
})
export const parseStoryFilmSeason = (value) => v.parse(StoryFilmSeason, value)

/**
 * A 只读：本人所有分支的第一个“合法可拍前缀”候选（B/C 首免受理竞争用）。
 * A 只给事实与排序，不写资格、不锁定唯一首免、不收费；零提交/弃局不参与。
 * 每条分支追溯历史已提交前缀，取最早同时具备可审计成集证据与可拍选段的前缀；
 * 前缀事件缺失或扫描超限时 status=incomplete、候选不排名（fail-closed）。
 */
const StoryFilmFirstFreeCandidate = v.object({
  run_id: StoryFilmId,
  branch_id: StoryFilmId,
  /** historical_branch：快照中保存的所有分支都参与，不只是当前 active。 */
  branch_basis: v.literal('historical_branch'),
  branch_active: v.boolean(),
  play_language: v.nullable(v.string()),
  source_version: StoryFilmHash,
  qualified_through_seq: v.number(),
  committed_through_seq: v.number(),
  through_turn_id: StoryFilmId,
  settlement: v.picklist(['result', 'suspense', 'result_and_suspense']),
  segment_count: v.number(),
  char_count: v.number(),
  event_key: v.nullable(StoryFilmHash),
  event_at: v.nullable(IsoTime),
  event_seq: v.nullable(v.number()),
  first_post_open_event_seq: v.number(),
  open_baseline_at: IsoTime,
  qualification_basis: v.literal('post_open_source_event'),
  rank: v.nullable(v.number()),
  ordering_basis: v.picklist(['source_committed_event', 'unknown_event']),
})

export const StoryFilmFirstFreeCandidates = v.object({
  owner_user_id: StoryFilmId,
  status: v.picklist(['complete', 'incomplete']),
  ordering_complete: v.boolean(),
  reasons: v.array(v.picklist(['baseline_unset', 'missing_event_for_post_open_prefix', 'unassessable_source'])),
  known_count: v.number(),
  unknown_count: v.number(),
  min_committed_turns: v.number(),
  checked_runs: v.number(),
  checked_branches: v.number(),
  scanned_pages: v.number(),
  scan_page_size: v.number(),
  unassessable_runs: v.array(v.object({
    run_id: StoryFilmId,
    reason: v.picklist(['snapshot_unreadable', 'player_id_missing', 'branches_invalid', 'event_chain_unproven']),
  })),
  /** 功能实际开放基线；未配置时为 null，status=incomplete/baseline_unset。 */
  open_baseline_at: v.nullable(IsoTime),
  baseline_source: v.string(),
  /** 仍有同分支更后面的真实可拍前缀未返回时（incomplete），消费方必须带着它继续取，才能判定“首个”。 */
  next_cursor: v.nullable(v.object({
    run_id: StoryFilmId,
    branch_id: StoryFilmId,
    after_seq: v.number(),
  })),
  /** A 只证明来源侧可拍；B 的真实分镜预算与合法 plan 由 B/C 受理时判定。 */
  b_plan_budget_verified: v.literal(false),
  candidates: v.array(StoryFilmFirstFreeCandidate),
  a_locks: v.literal(false),
})
export const parseStoryFilmFirstFreeCandidates = (value) => v.parse(StoryFilmFirstFreeCandidates, value)

export const StoryFilmFirstFreeCandidateCheck = v.object({
  owner_user_id: StoryFilmId,
  run_id: StoryFilmId,
  branch_id: v.nullable(StoryFilmId),
  qualified: v.boolean(),
  reason: v.nullable(v.picklist(['run_not_found', 'source_unavailable', 'branch_not_found', 'no_committed_prose', 'below_first_free_minimum', 'no_qualified_prefix', 'no_post_open_qualified_prefix', 'event_chain_unproven', 'source_drift', 'input_incomplete', 'baseline_unset'])),
  drift: v.boolean(),
  rank: v.nullable(v.number()),
  status: v.picklist(['complete', 'incomplete']),
  ordering_complete: v.boolean(),
  reasons: v.array(v.picklist(['baseline_unset', 'missing_event_for_post_open_prefix', 'unassessable_source'])),
  ordering_basis: v.nullable(v.picklist(['source_committed_event', 'unknown_event'])),
  candidate: v.nullable(StoryFilmFirstFreeCandidate),
  lockable: v.boolean(),
  missing_inputs: v.array(v.picklist(['branch_id', 'expected_source_version', 'expected_through_turn_id', 'baseline_at'])),
  open_baseline_at: v.nullable(IsoTime),
  /** HTTP 只读恒 false；仅 verifyFirstFreeCandidateWithinTx 可返回 true。 */
  transaction_scoped: v.boolean(),
  a_locks: v.literal(false),
})
export const parseStoryFilmFirstFreeCandidateCheck = (value) => v.parse(StoryFilmFirstFreeCandidateCheck, value)
